import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast } from "sonner";

const TERMS_VERSION = "2025-05-14";

const sanitizeNext = (raw, fallback) => {
  if (!raw || typeof raw !== "string") return fallback;
  if (!raw.startsWith("/") || raw.startsWith("//")) return fallback;
  return raw;
};

const Signup = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = sanitizeNext(searchParams.get("next"), "/dashboard");
  const initialRole = ["parent", "student", "teacher"].includes(searchParams.get("role")) ? searchParams.get("role") : "parent";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: initialRole });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setTermsError("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setTermsError("");
    setLoading(true);
    try {
      await register({
        ...form,
        terms_accepted: true,
        terms_version: TERMS_VERSION,
      });
      if (form.role === "teacher") {
        toast.success("Welcome, Trail Leader! Let's set up your first classroom.");
        navigate("/teacher/dashboard", { replace: true });
      } else {
        toast.success("Expedition begun! Your 14-day Summit Pass trial is active.");
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start expedition");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col">
      <div className="container mx-auto py-6">
        <Link to="/"><Logo size={44} /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-mm-navy/10 rounded-2xl p-8 fade-up">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Expedition Briefing</div>
          <h1 className="font-heading text-3xl font-black text-mm-navy mt-2">Start your expedition</h1>
          <p className="text-mm-navy/70 mt-2 text-sm">14-day Summit Pass trial — explore every trail, no card required.</p>

          <div className="mt-6" data-testid="signup-google-block">
            <GoogleSignInButton label="signup_with" />
            <p className="text-[11px] text-mm-navy/55 text-center mt-2 leading-snug">
              By continuing with Google you agree to our{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-mm-navy underline">Terms</Link>
              {" "}and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-bold text-mm-navy underline">Privacy Policy</Link>.
            </p>
          </div>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <div className="flex-1 h-px bg-mm-navy/15" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-mm-steel">or use email</span>
            <div className="flex-1 h-px bg-mm-navy/15" />
          </div>

          <form onSubmit={submit} className="space-y-4" noValidate>
            <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="signup-name-input" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="signup-email-input" />
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="signup-password-input" />

            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Climbing as</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {["parent", "student", "teacher"].map((r) => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => setForm({ ...form, role: r })}
                    className={`btn-trail capitalize text-xs sm:text-sm ${form.role === r ? "btn-gold" : "btn-outline-navy"}`}
                    data-testid={`signup-role-${r}`}
                  >
                    {r === "parent" ? "Parent" : r === "student" ? "Student" : "Teacher"}
                  </button>
                ))}
              </div>
              {form.role === "teacher" && (
                <p className="mt-2 text-xs text-mm-navy/70" data-testid="signup-teacher-note">
                  Teachers get 30 anonymous student accounts free — no student PII collected. Read the{" "}
                  <Link to="/terms/teacher" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-mm-steel">Teacher Terms</Link>.
                </p>
              )}
            </div>

            <div className="pt-1">
              <label className="flex items-start gap-3 cursor-pointer select-none" htmlFor="signup-terms-checkbox">
                <input
                  id="signup-terms-checkbox"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (e.target.checked) setTermsError("");
                  }}
                  className="mt-1 h-4 w-4 accent-mm-gold shrink-0"
                  data-testid="signup-terms-checkbox"
                  aria-describedby={termsError ? "signup-terms-error" : undefined}
                  aria-invalid={!!termsError}
                />
                <span className="text-sm text-mm-navy/85 leading-snug">
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-mm-navy underline hover:text-mm-steel" data-testid="signup-terms-link">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-bold text-mm-navy underline hover:text-mm-steel" data-testid="signup-privacy-link">
                    Privacy Policy
                  </Link>.
                </span>
              </label>
              {termsError && (
                <p id="signup-terms-error" role="alert" className="mt-2 text-sm text-red-700 font-bold" data-testid="signup-terms-error">
                  {termsError}
                </p>
              )}
            </div>

            <button type="submit" disabled={loading} className="btn-trail btn-gold w-full mt-2" data-testid="signup-submit-btn">
              {loading ? "Packing your gear..." : "Begin expedition"}
            </button>
          </form>

          <div className="text-sm text-mm-navy/70 mt-6 text-center">
            Already on the trail?{" "}
            <Link to={`/login${nextPath !== "/dashboard" ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="font-bold text-mm-navy hover:text-mm-steel" data-testid="signup-to-login-link">Continue journey</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30 font-body"
      data-testid={testid}
    />
  </div>
);

export default Signup;
